// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * React hook module for fetching a single return order by key.
 * Provides a convenient interface for fetching return order details.
 */
import { getReturnOrder } from '@/return-order/api/get-return-order';
import { returnOrderDetailQueryKey } from '@/return-order/query-keys';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * React hook for fetching a single return order by key.
 * Uses React Query's suspense mode for data fetching.
 *
 * Features:
 * - Automatic data fetching and caching via React Query
 * - Suspense mode for loading states
 * - Automatic refetch on window focus
 *
 * @param returnOrderKey - The unique key of the return order to fetch
 *
 * @returns Object containing:
 * - returnOrder: The return order data
 * - Query status information (error, refetch, etc.)
 *
 * @throws Will suspend while loading data
 * @throws Will throw errors from the API if request fails
 */
export function useReturnOrder(returnOrderKey: string) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: returnOrderDetailQueryKey(returnOrderKey),
    queryFn: () => getReturnOrder(returnOrderKey),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  return { returnOrder: data, ...query };
}
